/*
 * Copyright (c) 2026, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import type { FormStore } from "@formisch/react"
import { Form, Field as FormField, getInput, useForm } from "@formisch/react"
import { CirclePlusIcon } from "lucide-react"
import { useState } from "react"
import * as v from "valibot"

import ArtifactoryLogo from "../../assets/images/logo_artifactory.svg?react"
import { OrganizationSchema } from "../../features/organization/organization-schema"
import { OrganizationViewModel } from "../../features/organization/organization-viewmodel"
import { isFieldInvalid } from "../../utils/is-field-invalid"
import { useViewModel } from "../app/app-hooks"
import { BusyButton } from "../components/busy-button"
import { PasswordInput } from "../components/password-input"
import { Button } from "../primitives/button"
import { Card, CardContent } from "../primitives/card"
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger
} from "../primitives/dialog"
import { Field, FieldError, FieldGroup, FieldLabel } from "../primitives/field"
import { Input } from "../primitives/input"

/**
 * Opens the Create Organization dialog.
 *
 * The dialog is controlled here so the form can close it after creating the
 * organization. `Dialog` handles focus trapping, Escape/outside-click dismissal,
 * and returning focus to the trigger.
 */
export const CreateOrganization = () => {
    const [open, setOpen] = useState(false)

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger render={<Button className="font-semibold uppercase" />}>
                <CirclePlusIcon data-icon="inline-start" />
                Create Organization
            </DialogTrigger>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle className="m-0 text-xl leading-6 font-semibold uppercase">
                        Create Organization
                    </DialogTitle>
                </DialogHeader>
                <CreateOrganizationForm onCreated={() => setOpen(false)} />
            </DialogContent>
        </Dialog>
    )
}

interface CreateOrganizationFormProps {
    /**
     * Called after the organization is created successfully.
     */
    onCreated: () => void
}

/**
 * Form for creating an organization and its Artifactory credentials.
 *
 * The dialog unmounts the form when closed, so each opening starts with empty
 * fields. Create stays disabled until all fields pass `OrganizationSchema` and
 * while the request is in progress.
 */
const CreateOrganizationForm = ({ onCreated }: CreateOrganizationFormProps) => {
    const vm = useViewModel(OrganizationViewModel)
    const form = useForm({ schema: OrganizationSchema, validate: "blur" })

    /**
     * Creates the organization, refreshes the list, and closes the dialog.
     *
     * Failed requests are already reported by the view model, so the form stays
     * open with the entered values and can be submitted again.
     */
    const create = async (input: v.InferOutput<typeof OrganizationSchema>) => {
        const result = await vm.createOrganization(input.name, input.artifactoryUsername, input.artifactoryPassword)

        if (result.success) {
            void vm.getOrganizations()
            onCreated()
        }
    }

    return (
        <Form of={form} onSubmit={create}>
            <DialogBody>
                <FieldGroup>
                    <OrganizationField form={form} path="name" label="Organization Name" />

                    <Card role="group" aria-label="Artifactory">
                        <CardContent>
                            <ArtifactoryLogo aria-hidden className="mb-1 h-8 self-center" />

                            <OrganizationField form={form} path="artifactoryUsername" label="Artifactory Login" />
                            <OrganizationField
                                form={form}
                                path="artifactoryPassword"
                                label="Artifactory Password"
                                password
                            />
                        </CardContent>
                    </Card>
                </FieldGroup>
            </DialogBody>

            <DialogFooter>
                <DialogClose render={<Button variant="secondary" size="action" />}>Cancel</DialogClose>
                <BusyButton
                    type="submit"
                    size="action"
                    busy={form.isSubmitting}
                    disabled={!v.is(OrganizationSchema, getInput(form))}
                >
                    Create
                </BusyButton>
            </DialogFooter>
        </Form>
    )
}

interface OrganizationFieldProps {
    /**
     * Form state shared by all fields in the dialog.
     */
    form: FormStore<typeof OrganizationSchema>
    /**
     * Schema field this input edits; also used to derive its id.
     */
    path: keyof v.InferInput<typeof OrganizationSchema>
    /**
     * Label displayed with the input.
     */
    label: string
    /**
     * Masks the input and adds a button to reveal its value.
     */
    password?: boolean
}

/**
 * Renders a labelled organization form field with its validation error.
 *
 * `autoComplete` keeps the browser's password manager from treating the
 * Artifactory credentials as the user's own login credentials.
 */
const OrganizationField = ({ form, path, label, password = false }: OrganizationFieldProps) => {
    const id = `organization-${path}`

    return (
        <FormField of={form} path={[path]}>
            {(field) => {
                const invalid = isFieldInvalid(field)

                return (
                    <Field data-invalid={invalid}>
                        <FieldLabel htmlFor={id}>{label}</FieldLabel>
                        {password ? (
                            <PasswordInput
                                {...field.props}
                                id={id}
                                value={field.input}
                                aria-invalid={invalid}
                                autoComplete="new-password"
                                className="h-10"
                            />
                        ) : (
                            <Input
                                {...field.props}
                                id={id}
                                type="text"
                                value={field.input}
                                aria-invalid={invalid}
                                autoComplete="off"
                                className="h-10"
                            />
                        )}
                        {invalid && <FieldError>{field.errors?.[0]}</FieldError>}
                    </Field>
                )
            }}
        </FormField>
    )
}
